import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Wallet, ShieldCheck, Zap } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { EXTERNAL_LINKS } from '../config';
import './ProcessSteps.css';

/* ─── Data ────────────────────────────────────────── */
const STEPS = [
  {
    num: '01',
    total: '03',
    icon: Wallet,
    time: { en: '2 min', es: '2 min' },
    timeLabel: { en: 'Setup time', es: 'Tiempo de configuración' },
    title: { en: 'Connect your wallet', es: 'Conecta tu wallet' },
    desc: {
      en: 'Link your native Hedera wallet (HashPack or Kabila) or any WalletConnect compatible wallet. Sign your ownership in one click, no email, no password.',
      es: 'Vincula tu wallet nativa de Hedera (HashPack o Kabila) o cualquiera compatible con WalletConnect. Firmas tu propiedad en un clic, sin email, sin contraseña.',
    },
    bullets: {
      en: [
        'Native Hedera support — Solana, Ethereum, Arbitrum & Base coming soon',
        'Detects if you hold WhataLab collectibles',
        'Non-custodial, allowance-based operations. Your assets stay in your wallet.',
      ],
      es: [
        'Soporte nativo en Hedera y próximamente para Solana, Ethereum, Arbitrum y Base',
        'Identifica si eres holder de los colectibles WhataLab',
        'Non-custodial, operaciones con allowance. Tus activos siempre en tu wallet.',
      ],
    },
    image: '/screenshots/connect-wallet-modal.png',
    accent: '#26E2B3',
  },
  {
    num: '02',
    total: '03',
    icon: ShieldCheck,
    time: { en: 'Instant', es: 'Instantáneo' },
    timeLabel: { en: 'Verification', es: 'Verificación' },
    title: { en: 'Verify your holdings', es: 'Verifica tus holdings' },
    desc: {
      en: 'W·Panel scans your wallet on-chain in real time. NFTs, HTS tokens and EVM assets — all verified automatically against Mirror Node.',
      es: 'W·Panel escanea tu wallet on-chain en tiempo real. NFTs, tokens HTS y activos EVM — todo verificado automáticamente contra Mirror Node.',
    },
    bullets: {
      en: ['NFT & HTS token support', 'EVM asset detection', 'Mirror Node · Live data'],
      es: ['Soporte NFT & HTS tokens', 'Detección activos EVM', 'Mirror Node · Datos en vivo'],
    },
    image: '/screenshots/portfolio-view.png',
    accent: '#a78bfa',
  },
  {
    num: '03',
    total: '03',
    icon: Zap,
    time: { en: 'Level up', es: 'Sube de nivel' },
    timeLabel: { en: 'Premium access', es: 'Acceso premium' },
    title: { en: 'Activate your tools', es: 'Activa tus herramientas' },
    desc: {
      en: 'With WhataLab collectibles you hold the key to all tools, with perks, discounts and privileges only accessible with WATT Early Pack, WhataKit and WATT collections. All access unlocks when your holdings are verified.',
      es: 'Con los colectibles de WhataLab tienes la llave a la totalidad de herramientas, con ventajas, descuentos y privilegios solo accesibles con las colecciones WATT Early Pack, WhataKit y WATT. Todos los accesos se desbloquean al verificar tus holdings.',
    },
    bullets: {
      en: ['Get your collectible', 'Verify your ownership', 'Limited freemium access available without collectibles'],
      es: ['Hazte con tu colectible', 'Verifica tu propiedad', 'Acceso freemium limitado disponible sin colectibles'],
    },
    image: '/screenshots/wcase_spot.gif',
    accent: '#fbbf24',
  },
];

/* ─── Step card ───────────────────────────────────── */
const StepCard = ({ step, index, isActive, onClick, language }) => {
  const Icon = step.icon;
  const isEN = language === 'en';
  const hasImage = !!step.image;

  return (
    <motion.div
      className={`ps-card ${isActive ? 'ps-card--active' : ''} ${hasImage && isActive ? 'ps-card--with-image' : ''}`}
      style={{ '--accent': step.accent }}
      onClick={onClick}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.35, delay: index * 0.1 }}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
    >
      {/* Big background number */}
      <div className="ps-card-bg-num">{step.num}</div>

      {/* Content wrapper for two-column layout */}
      <div className={`ps-card-content ${hasImage && isActive ? 'ps-card-content--split' : ''}`}>
        
        {/* Left column: text content */}
        <div className="ps-card-text">
          {/* Top row: icon + step counter */}
          <div className="ps-card-top">
            <div className="ps-icon-wrap">
              <Icon size={20} />
            </div>
            <span className="ps-step-counter">{step.num}/{step.total}</span>
          </div>

          {/* Time badge */}
          <div className="ps-time-wrap">
            <span className="ps-time">{step.time[language] || step.time.en}</span>
            <span className="ps-time-label">{step.timeLabel[language] || step.timeLabel.en}</span>
          </div>

          {/* Title */}
          <h3 className="ps-card-title">
            {step.title[language] || step.title.en}
          </h3>

          {/* Description — visible when active */}
          <p className={`ps-card-desc ${isActive ? 'ps-card-desc--visible' : ''}`}>
            {step.desc[language] || step.desc.en}
          </p>

          {/* Bullets — visible when active */}
          <ul className={`ps-bullets ${isActive ? 'ps-bullets--visible' : ''}`}>
            {(step.bullets[language] || step.bullets.en).map((b, i) => (
              <li key={i} className="ps-bullet">
                <span className="ps-bullet-dot" />
                {b}
              </li>
            ))}
          </ul>
        </div>

        {/* Right column: image — visible when active */}
        {hasImage && (
          <div className={`ps-card-image ${isActive ? 'ps-card-image--visible' : ''}`}>
            <img src={step.image} alt={step.title[language] || step.title.en} loading="lazy" />
          </div>
        )}
      </div>
    </motion.div>
  );
};

/* ─── Main ────────────────────────────────────────── */
const ProcessSteps = () => {
  const { language } = useLanguage();
  const isEN = language === 'en';
  const [active, setActive] = useState(0);

  return (
    <section className="ps-section section" id="process">
      <div className="container">

        {/* Header */}
        <motion.div
          className="ps-header"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.35 }}
        >
          <div className="ps-eyebrow">
            <span className="ps-eyebrow-dot" />
            {isEN ? 'get started' : 'cómo empezar'}
          </div>
          <div className="ps-header-row">
            <h2 className="ps-title">
              {isEN
                ? <><span className="ps-title-accent">From wallet</span><br />to full access.</>
                : <><span className="ps-title-accent">De wallet</span><br />a acceso total.</>
              }
            </h2>
            <a
              href={EXTERNAL_LINKS.TOOLS_PLATFORM}
              target="_blank"
              rel="noopener noreferrer"
              className="ps-cta"
            >
              {isEN ? 'Start now' : 'Empezar ahora'}
              <ArrowRight size={15} />
            </a>
          </div>
        </motion.div>

        {/* Step cards */}
        <div className="ps-grid">
          {STEPS.map((step, i) => (
            <StepCard
              key={step.num}
              step={step}
              index={i}
              isActive={i === active}
              onClick={() => setActive(i)}
              language={language}
            />
          ))}
        </div>

      </div>
    </section>
  );
};

export default ProcessSteps;
